import { useCallback, useState, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { toast } from 'react-toastify';

import { IWebinarRegistrationProps } from './WebinarRegistrationStatus';
import { usePostWebinarDeclineInvitation, useRoomQuery } from '@src/hooks/queries';

import WebinarSuccessImg from '@src/assets/images/webinar/WebinarSuccess.png';
import DuplicateRegistrationImg from '@src/assets/images/webinar/DuplicateRegistration.png';
import DeclineRegistrationImg from '@src/assets/images/webinar/DeclineRegistration.png';

import { IWebinar } from '@src/webinar/types';
import useSendPageFinished from '@src/hooks/useSendPageFinished';

interface IStatusConfig {
  image: string;
  title: string;
  subtitle: string;
  button?: {
    text: string;
    variant: 'primary' | 'alternative' | 'success' | 'danger' | 'cancel' | 'disabled';
    onClick: () => void;
    disabled?: boolean;
  };
}

const useWebinarRegistrationStatus = () => {
  const [isDecline, setIsDecline] = useState(false);

  const location = useLocation();
  const { webinarId = '' } = useParams();
  const { data: roomInfo } = useRoomQuery(webinarId);

  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('uid');
  const roomData = (location.state?.roomData as IWebinar | undefined) || roomInfo;
  const { mutate: webinarDeclineInvitation } = usePostWebinarDeclineInvitation();

  useSendPageFinished({ isMeetingEnded: false });

  const handleDeclineInvitation = useCallback(() => {
    if (!userId) {
      toast.error('User ID is required to decline the invitation', {
        position: 'top-left',
      });
      return;
    }

    webinarDeclineInvitation(userId, {
      onSuccess: () => {
        toast.success('Webinar invitation declined successfully');
        setIsDecline(true);
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.error || 'Failed to decline the invitation';
        toast.error(errorMessage);
      },
    });
  }, [userId, webinarDeclineInvitation]);

  const statusData: Record<IWebinarRegistrationProps['status'], IStatusConfig> = useMemo(
    () => ({
      success: {
        image: WebinarSuccessImg,
        title: `Successfully registered to ${roomData?.roomTitle} webinar!`,
        subtitle: 'Please check your email for the registration details',
      },
      alreadyRegistered: {
        image: DuplicateRegistrationImg,
        title: `You are already registered to ${roomData?.roomTitle} webinar.`,
        subtitle: 'You can access the webinar link through your email.',
      },
      declineInvitation: {
        image: DeclineRegistrationImg,
        title: 'Decline webinar registration',
        subtitle: 'Are you sure you want to cancel your webinar registration? You are already registered.',
        button: {
          text: isDecline ? 'Registration Declined' : 'Decline Registration',
          variant: isDecline ? 'disabled' : 'danger',
          onClick: handleDeclineInvitation,
          disabled: isDecline,
        },
      },
    }),
    [roomData, isDecline, handleDeclineInvitation]
  );

  return { statusData, roomData };
};

export default useWebinarRegistrationStatus;
